import type { Audience, ContentType, Story, Topic } from "../../shared/model.ts";
import { storyRoles, type Role } from "../../shared/roles.ts";

export type DateRange = "any" | "7d" | "30d" | "90d";
export const DATE_RANGE_LABELS: Record<DateRange, string> = {
  any: "Any time",
  "7d": "Past 7 days",
  "30d": "Past 30 days",
  "90d": "Past 90 days",
};

export interface Filters {
  audience: Audience;
  role: Role | "all";
  topic: Topic | "all";
  contentTypes: ContentType[]; // empty = all
  dateRange: DateRange;
  search: string;
}

export const DEFAULT_FILTERS: Filters = {
  audience: "everyone",
  role: "all",
  topic: "all",
  contentTypes: [],
  dateRange: "any",
  search: "",
};

export function sinceFor(range: DateRange, now = Date.now()): number | null {
  const days = { any: 0, "7d": 7, "30d": 30, "90d": 90 }[range];
  return days ? now - days * 86_400_000 : null;
}

/**
 * The server query carries at most ONE indexed equality/array filter:
 * topic if one is chosen, otherwise role, otherwise audience. Everything else
 * is refined client-side over the loaded page. See docs/QUERIES.md.
 */
export type ServerFilter =
  | { kind: "none" }
  | { kind: "topic"; topic: Topic }
  | { kind: "role"; role: Role }
  | { kind: "audience"; audience: Exclude<Audience, "everyone"> };

export function serverFilterFor(f: Pick<Filters, "topic" | "audience"> & Partial<Pick<Filters, "role">>): ServerFilter {
  if (f.topic !== "all") return { kind: "topic", topic: f.topic };
  if (f.role && f.role !== "all") return { kind: "role", role: f.role };
  if (f.audience !== "everyone") return { kind: "audience", audience: f.audience };
  return { kind: "none" };
}

/** Client-side refinement over already-loaded stories. */
export function refine(stories: Story[], f: Filters, now = Date.now()): Story[] {
  const since = sinceFor(f.dateRange, now);
  const q = f.search.trim().toLowerCase();
  return stories.filter(
    (s) =>
      (f.topic === "all" || s.primaryTopic === f.topic) &&
      (f.audience === "everyone" || s.audiences.includes(f.audience)) &&
      (f.role === "all" || storyRoles(s).includes(f.role)) &&
      (f.contentTypes.length === 0 || f.contentTypes.includes(s.contentType)) &&
      (since === null || s.sortAt >= since) &&
      (q === "" || matchesSearch(s, q)),
  );
}

/** Headline + tag search over loaded stories only. Not an archive search. */
export function matchesSearch(s: Story, q: string): boolean {
  const terms = q.toLowerCase().split(/\s+/).filter(Boolean);
  const hay = [s.title, s.sourceName, ...s.topicTags].join(" ").toLowerCase();
  return terms.every((t) => hay.includes(t));
}
